/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useMutation, useQuery } from 'react-query';

import DatePicker from 'react-datepicker';
import { isMobile } from 'react-device-detect';
import { StateInputCalendarIcon } from 'assets/components';
import { InputMask } from '@react-input/mask';
import ModalBodyLayout from '../shared/ModalBodyLayout';
import { cardProviders, manageCardScreens, DatePeriods, getCardCapabilities } from './constants';
import Badge from '../../components/Badge';
import {
  CARD_GALILEO_SEND_STATEMENT_EMAIL,
  CARD_MONAVATE_SEND_STATEMENT_EMAIL,
  resolveStatementDownloadMutationKey,
} from './mutations';
import { showAlert } from '../../utilities/notifications';
import { handleFormBlur } from '../../utilities/formsUtils';
import { USER_DETAILS_QUERY_KEY } from '../../utilities/sharedQueries';
import { useUserPersonalInfo } from '../../utilities/sharedHooks/user';

const TransactionState = (props) => {
  const { t: translate } = useTranslation();
  const [dateRange, setDateRange] = useState([moment().startOf('month'), moment()]);
  const [fromInput, setFromInput] = useState();
  const [toInput, setToInput] = useState();
  const [notifyRemoveActive, setNotifyRemoveActive] = useState(false);
  const { isUS } = useUserPersonalInfo();

  const {
    data: { user },
  } = useQuery(USER_DETAILS_QUERY_KEY);

  const availablePeriods = useMemo(
    () => [
      {
        label: translate('last_week_label'),
        key: DatePeriods.LAST_WEEK,
      },
      {
        label: translate('last_month_label'),
        key: DatePeriods.LAST_MONTH,
      },
      {
        label: translate('no_months_label').replace('__NO__', 6),
        key: DatePeriods.HALF_YEAR,
      },
      {
        label: translate('year_label'),
        key: DatePeriods.YEAR,
      },
    ],
    []
  );

  const handleClickOnRangePicker = (periodKey) => {
    setNotifyRemoveActive(false);
    switch (periodKey) {
      case DatePeriods.LAST_WEEK:
        setDateRange([moment().subtract(7, 'days'), moment()]);
        break;

      case DatePeriods.HALF_YEAR:
        setDateRange([moment().subtract(6, 'months'), moment()]);
        break;

      case DatePeriods.YEAR:
        setDateRange([moment().subtract(1, 'year').startOf('day'), moment()]);
        break;

      default:
        setDateRange([moment().subtract(1, 'month').startOf('day'), moment()]);
    }
  };

  // const formatDate = (e) => {
  //   let newValue = e.target.value;
  //   if (newValue.length === 2 || newValue.length === 5) {
  //     newValue = `${newValue}/`;
  //   }
  //   e.target.value = newValue;
  // };

  const clearError = (e) => {
    e.target.className = e.target.className.replace('has-error', '');
    if (e.target.closest('.form-group').className.indexOf('has-focus') === -1) {
      e.target.closest('.form-group').classList.add('has-focus');
    }
  };

  const handleBlur = (e) => {
    handleFormBlur(e);
    e.target.closest('.form-group').classList.remove('has-focus');
  };

  const cardDownloadStatementMutationKey = useMemo(
    () => resolveStatementDownloadMutationKey(props.cardProvider, props.newPdfEndpointEnabled),
    [props?.cardProvider, props?.newPdfEndpointEnabled]
  );

  const { mutate: downloadStatementOfTransactions, isLoading: isDownloadingFile } = useMutation(
    cardDownloadStatementMutationKey,
    {
      onSettled(data, error) {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          const { statementFormat: format } = getCardCapabilities(props.cardProvider);
          const today = moment().format(isUS ? 'MMDDYYYY' : 'DDMMYYYY');
          const fileName = `CL-Card_Transactions_${user?.first_name}_${user?.last_name}_${today}.${format}`;

          props.downloadFile({
            data,
            fileName,
            format,
          });
          showAlert({
            message: translate('downloaded_statement_of_transactions_successfully_message'),
            type: 'success',
          });
        }
      },
    }
  );

  const cardSendStatementEmailMutationKey = useMemo(() => {
    if (props.cardProvider === cardProviders.MONAVATE) {
      return CARD_MONAVATE_SEND_STATEMENT_EMAIL;
    }

    return CARD_GALILEO_SEND_STATEMENT_EMAIL;
  }, [props?.cardProvider]);

  const { mutate: sendStatementOfTransactionsEmail, isLoading: isSendingEmail } = useMutation(
    cardSendStatementEmailMutationKey,
    {
      onSettled(data, error) {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          showAlert({
            message: translate('sent_statement_of_transactions_successfully_message'),
            type: 'success',
          });
        }
      },
    }
  );

  const handleDownloadStatement = (format) =>
    downloadStatementOfTransactions({
      fromDate: dateRange[0].format('YYYY-MM-DD'),
      toDate: dateRange[1].format('YYYY-MM-DD'),
      format,
    });
  const handleSendToEmail = () => {
    sendStatementOfTransactionsEmail({
      fromDate: dateRange[0].format('YYYY-MM-DD'),
      toDate: dateRange[1].format('YYYY-MM-DD'),
    });
  };
  const handleChangeFromDatePicker = (date) => {
    const newDate = moment(date, isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY');
    if (newDate.isValid()) {
      setDateRange([newDate, dateRange[1]]);
    } else {
      setDateRange([moment().startOf('month'), dateRange[1]]);
    }
    fromInput.closest('.form-group').classList.remove('has-focus');
    setNotifyRemoveActive(true);
  };

  const handleChangeToDatePicker = (date) => {
    const newDate = moment(date, isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY');
    if (newDate.isValid()) {
      setDateRange([dateRange[0], newDate]);
    } else {
      setDateRange([dateRange[0], moment()]);
    }
    toInput.closest('.form-group').classList.remove('has-focus');
    setNotifyRemoveActive(true);
  };

  const { statementByEmail } = getCardCapabilities(props.cardProvider);

  const renderFooter = () => (
    <div className="d-flex flex-row justify-content-center pb-2 pt-4">
      {statementByEmail && (
        <button
          type="button"
          className={`btn ${
            isMobile ? 'btn-primary' : 'border-grey border text-white me-3'
          }  w-100 ${isSendingEmail ? 'btn-disabled' : ''}`}
          loading={isSendingEmail ? 'true' : 'false'}
          disabled={isSendingEmail}
          onClick={handleSendToEmail}>
          {translate('send_to_email_button')}
        </button>
      )}

      {!isMobile &&
        (props.cardProvider === cardProviders.MONAVATE ? (
          <button
            data-testid="download_pdf_button"
            type="button"
            className={`btn btn-primary ms-3 w-100 ${isDownloadingFile ? 'btn-disabled' : ''}`}
            disabled={isDownloadingFile}
            onClick={() => handleDownloadStatement('csv')}>
            {isDownloadingFile ? (
              <div className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>{translate('download_csv_button')}</>
            )}
          </button>
        ) : (
          <button
            data-testid="download_pdf_button"
            type="button"
            className={`btn btn-primary ms-3 w-100 ${isDownloadingFile ? 'btn-disabled' : ''}`}
            disabled={isDownloadingFile}
            onClick={() => handleDownloadStatement('pdf')}>
            {isDownloadingFile ? (
              <div className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <>{translate('download_pdf_button')}</>
            )}
          </button>
        ))}
    </div>
  );

  return (
    <ModalBodyLayout
      title={translate('statement_of_transactions_title')}
      withBack
      isScrollable={false}
      renderFooter={renderFooter}
      onPrevClick={() => props.openScreen(manageCardScreens.MAIN)}>
      <div className="my-3">
        <p>
          {translate('choose_date_range_to_download', {
            format: getCardCapabilities(props.cardProvider).statementFormat.toUpperCase(),
          })}
        </p>

        <div className="form-group form-group-input-date mt-5 mb-0">
          <DatePicker
            open={false}
            ref={(e) => {
              if (e) setFromInput(e.input);
            }}
            placeholderText=""
            selected={dateRange[0].toDate()}
            className="form-control-input"
            // onBlur={handleBlur}
            onChangeRaw={(event) => handleChangeFromDatePicker(event.target.value)}
            customInput={
              <InputMask
                mask={isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
                showMask
                replacement={{ D: /\d/, M: /\d/, Y: /\d/ }}
              />
            }
            onFocus={clearError}
            peekNextMonth
            showMonthDropdown
            useWeekdaysShort
            showYearDropdown
            maxDate={moment().toDate()}
            dropdownMode="select"
            dateFormat={isUS ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
            required
            id="dateFrom"
          />
          <StateInputCalendarIcon />
          <label className="floating-label">
            {translate(isUS ? 'date_from_input_us_label' : 'date_from_input_label')}
          </label>
        </div>
        <p className="text-shadow text-f6 mt-4">{translate('range_picker_label')}</p>
        <div className="d-flex justify-content-between align-content-between mt-3 pb-2 w-100">
          {availablePeriods.map((period) => (
            <Badge
              key={period.key}
              notifyRemoveActive={notifyRemoveActive}
              content={period.label}
              onClick={() => {
                handleClickOnRangePicker(period.key);
              }}
              className="px-3 py-1 bg-lighter rounded-4 text-white btn border-grey text-f5 cursor-pointer"
            />
          ))}
        </div>

        <div className="form-group form-group-input-date mt-4 mb-0">
          <DatePicker
            open={false}
            placeholderText=""
            ref={(e) => {
              if (e) setToInput(e.input);
            }}
            selected={dateRange[1].toDate()}
            className="form-control-input"
            onBlur={handleBlur}
            onChangeRaw={(e) => handleChangeToDatePicker(e.target.value)}
            customInput={
              <InputMask
                mask={isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
                showMask
                replacement={{ D: /\d/, M: /\d/, Y: /\d/ }}
              />
            }
            onFocus={clearError}
            peekNextMonth
            showMonthDropdown
            useWeekdaysShort
            showYearDropdown
            maxDate={moment().toDate()}
            dropdownMode="select"
            dateFormat={isUS ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
            required
            id="dateTo"
          />
          <StateInputCalendarIcon />
          <label className="floating-label">
            {translate(isUS ? 'date_to_input_us_label' : 'date_to_input_label')}
          </label>
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default TransactionState;
TransactionState.propTypes = {
  openScreen: PropTypes.func.isRequired,
  cardProvider: PropTypes.string.isRequired,
  downloadFile: PropTypes.func.isRequired,
  newPdfEndpointEnabled: PropTypes.bool.isRequired,
};
